import { useId, useState, type ReactNode } from "react";
import type { ApiClient } from "../api/client";
import { useKeyedWrite, useWrite } from "../api/hooks";
import { parseKwachaToNgwee } from "../format/money";
import { Dialog } from "./Dialog";
import { ProblemAlert, UnknownOutcome, UnknownUnkeyed } from "./ProblemAlert";
import { Button } from "@lcc/ui";

export type FieldKind = "text" | "textarea" | "select" | "date" | "month" | "datetime" | "kwacha" | "number" | "checkbox";

export interface FieldSpec {
  name: string;
  label: string;
  kind?: FieldKind;
  required?: boolean;
  /** For selects: [value, label] pairs (an empty first option is added unless required with a default). */
  options?: ReadonlyArray<readonly [string, string]>;
  hint?: ReactNode;
  min?: string;
  max?: string;
  /** Hide the field (e.g. only for some choices). */
  hidden?: (v: Values) => boolean;
  maxLength?: number;
}

export type Values = Record<string, string>;

/** The ngwee of a Kwacha field, or null (never floats). */
export function kwacha(v: string | undefined): number | null {
  return v ? parseKwachaToNgwee(v) : null;
}

export type Build = (v: Values) => { body: unknown } | { error: string };

/**
 * A dialog with a small form for one API write. It owns the write (so a keyed write has one Idempotency-Key per
 * dialog, reused only while the outcome is unknown), checks required fields, shows the server's problem in plain
 * words, and reports the result.
 */
export function ActionDialog<R>({
  title,
  intro,
  fields,
  initial = {},
  submitLabel,
  build,
  write,
  intent,
  onClose,
  onDone,
  children,
  closeOnSuccess = true,
  onDiscard,
}: {
  title: string;
  intro?: ReactNode;
  fields: FieldSpec[];
  initial?: Values;
  submitLabel: string;
  build: Build;
  /** The API call; `key` is the Idempotency-Key when the dialog has an `intent`. */
  write: (api: ApiClient, body: unknown, key: string) => Promise<R>;
  /**
   * For writes the contract requires an Idempotency-Key for: the intent (operation + subject, e.g. `demand:<lease_id>`)
   * that keys the request and its unknown-outcome lock (R2-1).
   */
  intent?: string;
  onClose: () => void;
  onDone?: (result: R) => void;
  children?: (values: Values, set: (name: string, value: string) => void) => ReactNode;
  closeOnSuccess?: boolean;
  /** After "Discard and start again": values to change (e.g. a new `client_id`, since the first request may have used it). */
  onDiscard?: (values: Values) => Values;
}) {
  const plain = useWrite((api, body: unknown) => write(api, body, ""));
  const keyed = intent !== undefined;
  const withKey = useKeyedWrite((api, body: unknown, key) => write(api, body, key), intent ?? `unkeyed:${title}`);
  const m = keyed ? withKey : plain;
  const locked = keyed && withKey.locked;
  const send = (body: unknown) =>
    m.mutate(body, {
      onSuccess: (r) => {
        onDone?.(r as R);
        if (closeOnSuccess) onClose();
      },
    });
  // Locked after an unknown outcome: the form shows the locked request's values (review RW2), kept with the lock per
  // person and intent, so what "Try again (same request)" resends is what the person sees.
  const [values, setValues] = useState<Values>(() => (locked ? ((withKey.lockedForm as Values | undefined) ?? initial) : initial));
  const [err, setErr] = useState<string | null>(null);
  const baseId = useId();
  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }));
  const shown = fields.filter((f) => !f.hidden?.(values));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Locked after an unknown outcome: the same request again, whatever the form holds (review R-W10).
    if (locked) return send(undefined);
    for (const f of shown) {
      const v = (values[f.name] ?? "").trim();
      if (f.required && !v && f.kind !== "checkbox") return setErr(`${f.label.replace(/ \(required\)$/, "")} is required.`);
      if (f.kind === "kwacha" && v && kwacha(v) === null) return setErr(`${f.label}: enter an amount in Kwacha with at most 2 decimals, e.g. 1,250.50.`);
    }
    const out = build(values);
    if ("error" in out) return setErr(out.error);
    setErr(null);
    if (keyed) withKey.remember(values);
    send(out.body);
  };

  return (
    <Dialog title={title} onClose={onClose} focusContainer={locked}>
      <form className="fer-form" onSubmit={onSubmit} noValidate>
        {intro}
        <fieldset className="fer-bare-fieldset" disabled={locked}>
        {shown.map((f) => {
          const id = `${baseId}-${f.name}`;
          const v = values[f.name] ?? "";
          const label = `${f.label}${f.required && f.kind !== "checkbox" ? " (required)" : ""}`;
          if (f.kind === "checkbox") {
            return (
              <label key={f.name} className="fer-check">
                <input type="checkbox" checked={v === "true"} onChange={(e) => set(f.name, String(e.target.checked))} />
                {f.label}
              </label>
            );
          }
          return (
            <div key={f.name} className="fer-field">
              <label htmlFor={id}>
                <span>{label}</span>
              </label>
              {f.kind === "textarea" ? (
                <textarea id={id} rows={3} value={v} maxLength={f.maxLength ?? 1000} onChange={(e) => set(f.name, e.target.value)} />
              ) : f.kind === "select" ? (
                <select id={id} value={v} onChange={(e) => set(f.name, e.target.value)}>
                  <option value="">Choose…</option>
                  {(f.options ?? []).map(([value, text]) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={id}
                  type={f.kind === "date" ? "date" : f.kind === "month" ? "month" : f.kind === "datetime" ? "datetime-local" : f.kind === "number" ? "number" : "text"}
                  inputMode={f.kind === "kwacha" ? "decimal" : undefined}
                  value={v}
                  min={f.min}
                  max={f.max}
                  maxLength={f.kind === "text" || !f.kind ? (f.maxLength ?? 200) : undefined}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              )}
              {f.hint && <span className="fer-hint">{f.hint}</span>}
            </div>
          );
        })}
        {children?.(values, set)}
        </fieldset>
        {err && (
          <p className="fer-form-error" role="alert">
            {err}
          </p>
        )}
        <ProblemAlert error={m.error} />
        {keyed ? (
          <UnknownOutcome
            write={{
              ...withKey,
              discard: () => {
                withKey.discard();
                if (onDiscard) setValues((v) => onDiscard(v));
              },
            }}
            retry={() => send(undefined)}
          />
        ) : (
          <UnknownUnkeyed show={plain.unknown} />
        )}
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={m.isPending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
